import type { UnitGrams } from './catalog.ts';
import type { Amount } from './quantity.ts';
import { UNITS, type Unit } from './units.ts';

/** How a line's weight was worked out; anything but `measured` is an estimate. */
export type GramsBasis = 'measured' | 'unit_weight' | 'density';

/** The catalog facts needed to weigh a line. */
export interface WeighFacts {
  unitGrams: UnitGrams;
  densityGPerMl: number | null;
}

/**
 * The weight of an ingredient line in grams (PLAN.md §5 step 4): mass units convert directly;
 * volume units use the ingredient's weight per unit (1 EL Honig = 21 g) or its density;
 * counts ("2 Zwiebeln", "1 Dose") use the weight per piece or pack. Ranges use their middle.
 * Null when the line has no quantity or the catalog doesn't know the unit.
 */
export function lineGrams(
  amount: Amount,
  facts: WeighFacts,
): { grams: number; basis: GramsBasis } | null {
  if (amount.quantity === null) return null;
  const quantity =
    amount.quantityMax != null ? (amount.quantity + amount.quantityMax) / 2 : amount.quantity;
  const unit: Unit | null = amount.unit;
  const round = (g: number) => Math.round(g * 10) / 10;

  if (unit && UNITS[unit].dimension === 'mass') {
    return { grams: round(quantity * UNITS[unit].factor), basis: 'measured' };
  }
  const perUnit = facts.unitGrams[unit ?? 'piece'];
  if (perUnit) return { grams: round(quantity * perUnit), basis: 'unit_weight' };
  if (unit && UNITS[unit].dimension === 'volume') {
    const ml = quantity * UNITS[unit].factor;
    if (facts.densityGPerMl) return { grams: round(ml * facts.densityGPerMl), basis: 'density' };
    // Spoons and cups without a known weight: assume water-like.
    return { grams: round(ml), basis: 'density' };
  }
  return null;
}
